import { oc } from "@orpc/contract";
import { z } from "zod";
import { EDI844AcknowledgementStatusSchema } from "./edi844-submissions.contract";

/**
 * The log of every EDI 997 a supplier sent us, matched or not — the S&D admin
 * view for checking that the matching rule (ST02 → our batch number) holds on
 * real traffic.
 */

export const EDI997MatchStatusSchema = z.enum([
  "matched",
  "partially_matched",
  "unmatched",
]);
export type EDI997MatchStatus = z.infer<typeof EDI997MatchStatusSchema>;

export const EDI997GroupResultSchema = z.enum([
  "accepted",
  "accepted_with_errors",
  "partially_accepted",
  "rejected",
]);

/** One claim batch a 997 named, and the submission it matched (if any). */
const EDI997AcknowledgedBatchSchema = z.object({
  batchNumber: z.string().nullable(),
  submissionId: z.uuid().nullable(),
  submissionNumber: z.int().nullable(),
  status: EDI844AcknowledgementStatusSchema.exclude(["awaiting"]),
  transactionSetCount: z.int(),
  matchReason: z.string(),
  errors: z.array(z.string()),
});

export const EDI997AcknowledgementSchema = z.object({
  id: z.uuid(),
  receivedAt: z.coerce.date(),
  acknowledgedAt: z.coerce.date().nullable(),
  senderId: z.string().nullable(),
  groupControlNumber: z.string().nullable(),
  groupResult: EDI997GroupResultSchema,
  matchStatus: EDI997MatchStatusSchema,
  transactionSetCount: z.int(),
  batches: z.array(EDI997AcknowledgedBatchSchema),
  inboundEmail: z
    .object({ id: z.uuid(), subject: z.string().nullable() })
    .nullable(),
});
export type EDI997Acknowledgement = z.infer<typeof EDI997AcknowledgementSchema>;

/** Fixed-size pages: the log is a scan of recent traffic, not a report. */
export const EDI997_LOG_PAGE_SIZE = 50;

export const ListEDI997AcknowledgementsInputSchema = z.object({
  matchStatus: z.array(EDI997MatchStatusSchema).optional(),
  page: z.int().min(0).optional(),
});

export const ListEDI997AcknowledgementsResponseSchema = z.object({
  acknowledgements: z.array(EDI997AcknowledgementSchema),
  total: z.int().nonnegative(),
});

export const edi997AcknowledgementsContracts = {
  list: oc
    .input(ListEDI997AcknowledgementsInputSchema)
    .output(ListEDI997AcknowledgementsResponseSchema)
    .route({
      method: "GET",
      summary: "List received EDI 997 acknowledgements",
      description:
        "Lists every X12 997 suppliers sent for our 844 claims, newest first, with the claim batches each one matched (or why it didn't).",
    }),
} as const;
